import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFixedParam } from '@/lib/scopedParams';
import { Link } from 'react-router';
import { api } from '../../api';
import type { TrafficRow } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ClassTable, CountBadge, enumLabel, ms, RefreshButton, RegistryRef, RegistrySelect, ResetButton, TransactionTable, useParamSetter, useRegistryCrumbs, When } from './shared';

/** `/registry/sessions`: what is persisted about EPP sessions (configured pools, recent traffic, SESSION commands). */
export function SessionsPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('sessions.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const registry = params.get('registry') ?? '';
  // On a registry's page the registry would repeat the page on every row.
  const oneRegistry = Boolean(useFixedParam('registry'));
  const result = useApi((signal) => api.getEppSessions({ registry: registry || undefined }, { signal }), [registry]);
  const unlimited = t('unlimited');

  const trafficColumns: Column<TrafficRow>[] = [
    ...(oneRegistry ? [] : [{ key: 'registry', header: t('sessions.registry'), cell: (r: TrafficRow) => <RegistryRef name={r.registry_name} code={r.registry_code} /> }]),
    { key: 'class', header: t('sessions.class'), cell: (r) => <span title={r.traffic_class}>{enumLabel('trafficClass', r.traffic_class)}</span> },
    { key: 'total', header: t('sessions.total'), numeric: true, cell: (r) => formatCount(r.total) },
    { key: 'success', header: t('sessions.success'), numeric: true, cell: (r) => formatCount(r.success) },
    { key: 'rejected', header: t('sessions.rejected'), numeric: true, cell: (r) => <CountBadge n={r.rejected} tone="crit" /> },
    {
      key: 'unknown',
      header: t('sessions.unknown'),
      numeric: true,
      cell: (r) =>
        r.unresolved ? (
          <Link to={`/registry/transactions?registry=${encodeURIComponent(r.registry_code)}&traffic_class=${r.traffic_class}&state=UNRESOLVED`}>
            <CountBadge n={r.unresolved} tone="serious" />
          </Link>
        ) : (
          <CountBadge n={0} tone="serious" />
        ),
    },
    { key: 'inflight', header: t('sessions.inFlight'), numeric: true, cell: (r) => formatCount(r.in_flight) },
    { key: 'latency', header: t('sessions.maxLatency'), numeric: true, cell: (r) => (r.max_latency_ms !== null ? ms(r.max_latency_ms) : '—') },
    { key: 'last', header: t('sessions.last'), cell: (r) => <When at={r.last_started_at} /> },
  ];

  return (
    <>
      <PageHeader title={t('sessions.title')} crumbs={crumbs} subtitle={t('sessions.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      <div className="space-y-6">
        <Callout tone="warn" title={t('sessions.limitTitle')}>
          <p>{t('sessions.limit')}</p>
          <Link to="/ops/registries" className="mt-1 inline-flex items-center gap-1 font-medium text-primary hover:underline">
            {t('sessions.limitLink')} <ChevronRight className="size-3.5" aria-hidden="true" />
          </Link>
        </Callout>
        <FilterBar actions={<ResetButton onClick={reset} disabled={!registry} />}>
          <RegistrySelect value={registry} onChange={(v) => set('registry', v)} />
        </FilterBar>
        {result.status === 'loading' ? <Loading rows={3} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('sessions.loadError')} /> : null}
        {result.status === 'success' ? (
          <>
            <section className="space-y-3" aria-labelledby="pools-heading">
              <h2 id="pools-heading" className="text-base font-semibold">
                {t('sessions.pools')}
              </h2>
              {result.data.pools.length === 0 ? <EmptyState title={t('sessions.noPools')} /> : null}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 [&>*]:min-w-0">
                {result.data.pools.map((p) => (
                  <Section
                    key={p.environment_id}
                    title={
                      <span className="flex flex-wrap items-center gap-2">
                        {oneRegistry ? null : <RegistryRef name={p.registry_name} code={p.registry_code} />}
                        {p.active ? <Badge tone="ok">{t('epp.active')}</Badge> : <Badge tone="neutral">{t('epp.inactive')}</Badge>}
                      </span>
                    }
                    description={
                      <span className="font-mono text-xs">
                        {p.epp_host ?? t('epp.noHost')}:{p.epp_port ?? '—'}
                      </span>
                    }
                  >
                    <div className="space-y-3">
                      {p.settings_error ? <Callout tone="warn">{t('epp.settingsError')}</Callout> : null}
                      {p.settings ? (
                        <>
                          <ClassTable settings={p.settings} />
                          <p className="text-xs text-muted-foreground">
                            {/* With no command, idle or lifetime limit the recycle sentence says nothing, so say that instead. */}
                            {p.settings.max_commands_per_session === null && p.settings.max_session_idle_ms === null && p.settings.max_session_lifetime_ms === null
                              ? t('sessions.noRecycle', { acquire: ms(p.settings.acquire_timeout_ms) })
                              : t('sessions.recycle', {
                                  commands: p.settings.max_commands_per_session ?? unlimited,
                                  idle: p.settings.max_session_idle_ms !== null ? ms(p.settings.max_session_idle_ms) : unlimited,
                                  lifetime: p.settings.max_session_lifetime_ms !== null ? ms(p.settings.max_session_lifetime_ms) : unlimited,
                                  acquire: ms(p.settings.acquire_timeout_ms),
                                })}
                          </p>
                        </>
                      ) : null}
                    </div>
                  </Section>
                ))}
              </div>
            </section>

            <Section flush title={t('sessions.traffic', { hours: result.data.traffic_window_hours })}>
              <DataTable
                columns={trafficColumns}
                rows={result.data.traffic}
                rowKey={(r) => `${r.registry_code}/${r.traffic_class}`}
                empty={<EmptyState title={t('sessions.noTraffic')} />}
              />
            </Section>

            <Section flush title={t('sessions.sessionCommands')}>
              {result.data.session_transactions.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('sessions.noSessionTx')}>{t('sessions.noSessionTxBody')}</EmptyState>
                </div>
              ) : (
                <TransactionTable rows={result.data.session_transactions} />
              )}
            </Section>
          </>
        ) : null}
      </div>
    </>
  );
}
